import { Control, Controller } from "react-hook-form";
import { Label, Radio } from "flowbite-react";
import faculties from "../../data/faculties.json";
import { Select } from "../../components/form";
import semesters from "../../data/semesters.json";
import {
  AcademicEmailComposerFormValues,
  UnitOfStudy,
} from "./AcademicEmailComposerForm.Types";
import { EmailTemplate, EmailTemplateList } from "../../models/notification";

interface AcademicEmailComposerFormStep1Props {
  control: Control<AcademicEmailComposerFormValues>;
  formValues: AcademicEmailComposerFormValues;
  selectedFaculty: string;
  handleChangeEmailTemplate: (template: string) => void;
  compileSubject: (subject: string, unitOfStudyCode: string) => void;
  emailTemplates: EmailTemplateList;
  emailTemplate: EmailTemplate;
  unitsOfStudy: UnitOfStudy[];
}

const AcademicEmailComposerFormStep1 = ({
  control,
  formValues,
  selectedFaculty,
  handleChangeEmailTemplate,
  compileSubject,
  emailTemplates,
  emailTemplate,
  unitsOfStudy,
}: AcademicEmailComposerFormStep1Props) => {
  return (
    <div id="step-1" className="space-y-6">
      <Controller
        name="faculty"
        control={control}
        rules={{
          required: "Faculty is required",
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <Label className="block text-sm font-medium mb-2">
              Faculty <span className="text-red-500">*</span>
            </Label>
            <ul className="flex flex-row flex-nowrap gap-2 justify-center h-30 items-stretch overflow-x-auto">
              {faculties.map((faculty) => (
                <li
                  key={faculty.id}
                  className={`flex-1 flex-grow p-4 border rounded-md text-center hover:border-red-500 transition-colors cursor-pointer bg-white ${
                    selectedFaculty === faculty.id
                      ? "border-red-500"
                      : "border-gray-200"
                  }`}
                >
                  <Radio
                    name="faculty"
                    id={faculty.id}
                    value={faculty.id}
                    onChange={() => {
                      field.onChange(faculty.id);
                    }}
                    className="hidden"
                  />
                  <Label
                    htmlFor={faculty.id}
                    className="cursor-pointer flex flex-col text-left "
                  >
                    <img
                      src={`/images/faculty/${faculty.icon}`}
                      alt={faculty.name}
                      width={40}
                      height={40}
                    />
                    <div className="line-clamp-2 m-1">{faculty.name}</div>
                  </Label>
                </li>
              ))}
            </ul>
            <p className="text-red-500 text-xs mt-1">{error?.message}</p>
          </>
        )}
      />

      {formValues.faculty && (
        <Controller
          name="semester"
          control={control}
          rules={{
            required: "Semester is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, disabled } = field;
            return (
              <Select
                name={name}
                value={value}
                onBlur={onBlur}
                disabled={disabled}
                placeholder="Select a semester"
                label="Semester"
                options={semesters}
                errorText={error?.message}
                onChange={(value: string) => {
                  field.onChange(value);
                }}
              />
            );
          }}
        />
      )}

      {formValues.semester && (
        <Controller
          name="unitOfStudy"
          control={control}
          rules={{
            required: "Unit of study is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, disabled } = field;
            const sortedUnits = [...unitsOfStudy].sort((a, b) =>
              a.label.localeCompare(b.label),
            );
            return (
              <Select
                name={name}
                value={value}
                onBlur={onBlur}
                disabled={disabled}
                label="Unit of study"
                placeholder="Select a unit of study"
                options={
                  sortedUnits.map((unit) => ({
                    value: unit.value,
                    label: `${unit.value} ${unit.label}`,
                  })) || []
                }
                errorText={error?.message}
                onChange={(value: string) => {
                  field.onChange(value);
                  compileSubject(emailTemplate?.subject || "", value);
                }}
              />
            );
          }}
        />
      )}

      {formValues.unitOfStudy && (
        <Controller
          name="sendWhen"
          control={control}
          rules={{
            required: "Email template is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur } = field;
            return (
              <Select
                disabled
                name={name}
                value={value}
                onBlur={onBlur}
                label="Send the email when"
                placeholder="Select when to send"
                options={[{ value: "welcome", label: "Welcome" }]}
                errorText={error?.message}
                onChange={(value: string) => {
                  field.onChange(value);
                  handleChangeEmailTemplate(value);
                }}
              />
            );
          }}
        />
      )}
      {formValues.sendWhen && formValues.unitOfStudy && (
        <Controller
          name="emailTemplate"
          control={control}
          rules={{
            required: "Email template is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, disabled } = field;
            return (
              <Select
                name={name}
                value={value}
                onBlur={onBlur}
                disabled={disabled}
                label="Template"
                placeholder="Select an email template"
                options={
                  emailTemplates.map((template) => ({
                    value: template.name,
                    label: template.name,
                  })) || []
                }
                errorText={error?.message}
                onChange={(value: string) => {
                  field.onChange(value);
                  handleChangeEmailTemplate(value);
                }}
              />
            );
          }}
        />
      )}
      <button
        type="submit"
        className="focus:outline-none text-white bg-brand font-medium rounded-md text-md px-5 py-2.5 me-2 mb-2 w-full"
      >
        Next
      </button>
    </div>
  );
};

export default AcademicEmailComposerFormStep1;
